import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SCENARIO_TYPES = [
  { value: 'cultural', label: 'Culturale' },
  { value: 'family', label: 'Famiglia' },
  { value: 'outdoor', label: 'Outdoor/Trekking' },
  { value: 'spiritual', label: 'Spirituale' },
  { value: 'edge_case', label: 'Edge Case' },
  { value: 'regression', label: 'Regression' }
];

const OUTCOMES = [
  { value: 'pass', label: 'Pass', color: 'bg-green-100 text-green-800' },
  { value: 'partial', label: 'Partial', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'fail', label: 'Fail', color: 'bg-red-100 text-red-800' }
];

export default function SmokeTestPage() {
  const [view, setView] = useState('list'); // list | form | detail
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    outcome: '',
    scenario_type: '',
    tester_name: ''
  });
  
  // Form state
  const [form, setForm] = useState({
    scenario_title: '',
    scenario_type: 'cultural',
    tester_name: '',
    environment: 'preview',
    initial_message: '',
    followups: [''],
    outcome: 'pass',
    score_1_10: 7,
    starter_emerged: false,
    bench_used_well: false,
    seasonal_separated: false,
    repeated_questions: false,
    natural_tone: false,
    relevant_proposals: false,
    what_worked: '',
    what_failed: '',
    final_note: ''
  });
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.outcome) params.append('outcome', filters.outcome);
      if (filters.scenario_type) params.append('scenario_type', filters.scenario_type);
      if (filters.tester_name) params.append('tester_name', filters.tester_name);
      
      const res = await fetch(`${API_URL}/api/smoketest?${params.toString()}`);
      const data = await res.json();
      setTests(data.tests || []);
    } catch (err) {
      console.error('Failed to fetch tests:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (view === 'list') {
      fetchTests();
    }
  }, [view, fetchTests]);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFollowupChange = (index, value) => {
    const newFollowups = [...form.followups];
    newFollowups[index] = value;
    setForm(prev => ({ ...prev, followups: newFollowups }));
  };

  const addFollowup = () => {
    if (form.followups.length < 5) {
      setForm(prev => ({ ...prev, followups: [...prev.followups, ''] }));
    }
  };

  const removeFollowup = (index) => {
    const newFollowups = form.followups.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, followups: newFollowups.length ? newFollowups : [''] }));
  };

  const handleScreenshotUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - screenshots.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setScreenshots(prev => [...prev.slice(0, 2), { 
          data: ev.target.result, 
          name: file.name 
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Create test
      const payload = {
        ...form,
        followups: form.followups.filter(f => f.trim())
      };
      
      const res = await fetch(`${API_URL}/api/smoketest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) throw new Error('Failed to create test');
      const test = await res.json();
      
      // Upload screenshots
      for (const screenshot of screenshots) {
        await fetch(`${API_URL}/api/smoketest/${test.id}/screenshot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_data: screenshot.data,
            filename: screenshot.name
          })
        });
      }
      
      // Reset and go to list
      setForm({
        scenario_title: '',
        scenario_type: 'cultural',
        tester_name: form.tester_name, // Keep tester name
        environment: 'preview',
        initial_message: '',
        followups: [''],
        outcome: 'pass',
        score_1_10: 7,
        starter_emerged: false,
        bench_used_well: false,
        seasonal_separated: false,
        repeated_questions: false,
        natural_tone: false,
        relevant_proposals: false,
        what_worked: '',
        what_failed: '',
        final_note: ''
      });
      setScreenshots([]);
      setView('list');
      fetchTests();
    } catch (err) {
      alert('Errore nel salvataggio: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (filters.outcome) params.append('outcome', filters.outcome);
    if (filters.scenario_type) params.append('scenario_type', filters.scenario_type);
    if (filters.tester_name) params.append('tester_name', filters.tester_name);
    
    window.open(`${API_URL}/api/smoketest/export/csv?${params.toString()}`, '_blank');
  };

  const viewDetail = async (testId) => {
    try {
      const res = await fetch(`${API_URL}/api/smoketest/${testId}`);
      const data = await res.json();
      setSelectedTest(data);
      setView('detail');
    } catch (err) {
      console.error('Failed to fetch test:', err);
    }
  };

  // List View
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Smoke Test QA - Chiara</h1>
            <button
              onClick={() => setView('form')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Nuovo Test
            </button>
          </div>
          
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <select
                value={filters.outcome}
                onChange={(e) => setFilters(prev => ({ ...prev, outcome: e.target.value }))}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">Tutti gli outcome</option>
                {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              
              <select
                value={filters.scenario_type}
                onChange={(e) => setFilters(prev => ({ ...prev, scenario_type: e.target.value }))}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">Tutti i tipi</option>
                {SCENARIO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              
              <input
                type="text"
                placeholder="Tester name..."
                value={filters.tester_name}
                onChange={(e) => setFilters(prev => ({ ...prev, tester_name: e.target.value }))}
                className="px-3 py-2 border rounded-lg"
              />
              
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Export CSV
              </button>
            </div>
          </div>
          
          {/* Test List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Caricamento...</div>
          ) : tests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Nessun test trovato</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scenario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tester</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Outcome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tests.map(test => {
                    const outcomeStyle = OUTCOMES.find(o => o.value === test.outcome)?.color || '';
                    return (
                      <tr 
                        key={test.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => viewDetail(test.id)}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">{test.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{test.scenario_title}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {SCENARIO_TYPES.find(t => t.value === test.scenario_type)?.label || test.scenario_type}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{test.tester_name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${outcomeStyle}`}>
                            {test.outcome}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-700">{test.score_1_10}/10</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(test.created_at).toLocaleDateString('it-IT')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Detail View
  if (view === 'detail' && selectedTest) {
    const outcomeStyle = OUTCOMES.find(o => o.value === selectedTest.outcome)?.color || '';
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => { setView('list'); setSelectedTest(null); }}
            className="mb-4 text-blue-600 hover:underline"
          >
            ← Torna alla lista
          </button>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800">{selectedTest.scenario_title}</h1>
                <p className="text-sm text-gray-500">
                  {selectedTest.id} • {selectedTest.tester_name} • {new Date(selectedTest.created_at).toLocaleString('it-IT')}
                </p>
              </div>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${outcomeStyle}`}>
                {selectedTest.outcome} - {selectedTest.score_1_10}/10
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <span className="text-xs text-gray-500 uppercase">Tipo scenario</span>
                <p className="font-medium">{SCENARIO_TYPES.find(t => t.value === selectedTest.scenario_type)?.label}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Environment</span>
                <p className="font-medium">{selectedTest.environment}</p>
              </div>
            </div>
            
            <div className="mb-6">
              <span className="text-xs text-gray-500 uppercase">Messaggio iniziale</span>
              <p className="bg-gray-50 p-3 rounded mt-1">{selectedTest.initial_message}</p>
            </div>
            
            {selectedTest.followups?.length > 0 && (
              <div className="mb-6">
                <span className="text-xs text-gray-500 uppercase">Follow-ups</span>
                <ul className="list-disc list-inside mt-1">
                  {selectedTest.followups.map((f, i) => <li key={i} className="text-gray-700">{f}</li>)}
                </ul>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                ['starter_emerged', 'Starter emerged'],
                ['bench_used_well', 'Bench used well'],
                ['seasonal_separated', 'Seasonal separated'],
                ['repeated_questions', 'Repeated questions'],
                ['natural_tone', 'Natural tone'],
                ['relevant_proposals', 'Relevant proposals']
              ].map(([key, label]) => (
                <div key={key} className={`px-3 py-2 rounded text-sm ${selectedTest[key] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                  {selectedTest[key] ? '✓' : '✗'} {label}
                </div>
              ))}
            </div>
            
            {selectedTest.what_worked && (
              <div className="mb-4">
                <span className="text-xs text-gray-500 uppercase">Cosa ha funzionato</span>
                <p className="bg-green-50 p-3 rounded mt-1 text-green-800">{selectedTest.what_worked}</p>
              </div>
            )}
            
            {selectedTest.what_failed && (
              <div className="mb-4">
                <span className="text-xs text-gray-500 uppercase">Cosa non ha funzionato</span>
                <p className="bg-red-50 p-3 rounded mt-1 text-red-800">{selectedTest.what_failed}</p>
              </div>
            )}
            
            {selectedTest.final_note && (
              <div className="mb-4">
                <span className="text-xs text-gray-500 uppercase">Note finali</span>
                <p className="bg-gray-50 p-3 rounded mt-1">{selectedTest.final_note}</p>
              </div>
            )}
            
            {selectedTest.screenshots?.length > 0 && (
              <div>
                <span className="text-xs text-gray-500 uppercase">Screenshots</span>
                <div className="flex gap-2 mt-2">
                  {selectedTest.screenshots.map((url, i) => (
                    <a key={i} href={`${API_URL}${url}`} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={`${API_URL}${url}`} 
                        alt={`Screenshot ${i + 1}`}
                        className="h-32 rounded border hover:opacity-80"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Form View
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => setView('list')}
          className="mb-4 text-blue-600 hover:underline"
        >
          ← Torna alla lista
        </button>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-xl font-bold text-gray-800 mb-6">Nuovo Smoke Test</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo Scenario *</label>
                <input
                  type="text"
                  required
                  value={form.scenario_title}
                  onChange={(e) => handleFormChange('scenario_title', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Es: Richiesta itinerario culturale base"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <select
                  value={form.scenario_type}
                  onChange={(e) => handleFormChange('scenario_type', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {SCENARIO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tester Name *</label>
                <input
                  type="text"
                  required
                  value={form.tester_name}
                  onChange={(e) => handleFormChange('tester_name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Nome tester"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Environment *</label>
                <select
                  value={form.environment}
                  onChange={(e) => handleFormChange('environment', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="preview">Preview</option>
                  <option value="live">Live</option>
                </select>
              </div>
            </div>
            
            {/* Conversation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Messaggio Iniziale *</label>
              <textarea
                required
                rows={3}
                value={form.initial_message}
                onChange={(e) => handleFormChange('initial_message', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Il messaggio di apertura inviato a Chiara..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-ups</label>
              {form.followups.map((f, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={f}
                    onChange={(e) => handleFollowupChange(i, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                    placeholder={`Follow-up ${i + 1}...`}
                  />
                  {form.followups.length > 1 && (
                    <button type="button" onClick={() => removeFollowup(i)} className="text-red-500">×</button>
                  )}
                </div>
              ))}
              {form.followups.length < 5 && (
                <button type="button" onClick={addFollowup} className="text-blue-600 text-sm">+ Aggiungi follow-up</button>
              )}
            </div>
            
            {/* Outcome */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Outcome *</label>
                <select
                  value={form.outcome}
                  onChange={(e) => handleFormChange('outcome', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Score (1-10) *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={form.score_1_10}
                  onChange={(e) => handleFormChange('score_1_10', parseInt(e.target.value) || 5)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            
            {/* Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quality Checks</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['starter_emerged', 'Starter emerged first'],
                  ['bench_used_well', 'Bench used as alternatives'],
                  ['seasonal_separated', 'Seasonal kept separate'],
                  ['repeated_questions', 'Repeated questions'],
                  ['natural_tone', 'Natural conversational tone'],
                  ['relevant_proposals', 'Relevant proposals']
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(e) => handleFormChange(key, e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Text Feedback */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cosa ha funzionato</label>
              <textarea
                rows={2}
                value={form.what_worked}
                onChange={(e) => handleFormChange('what_worked', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cosa non ha funzionato</label>
              <textarea
                rows={2}
                value={form.what_failed}
                onChange={(e) => handleFormChange('what_failed', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note finali</label>
              <textarea
                rows={2}
                value={form.final_note}
                onChange={(e) => handleFormChange('final_note', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            {/* Screenshots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screenshots (max 3)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleScreenshotUpload}
                disabled={screenshots.length >= 3}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700"
              />
              {screenshots.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {screenshots.map((s, i) => (
                    <div key={i} className="relative">
                      <img src={s.data} alt={s.name} className="h-20 rounded border" />
                      <button
                        type="button"
                        onClick={() => removeScreenshot(i)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Submit */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setView('list')}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Salvataggio...' : 'Salva Test'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
